import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * This Service provides basic cookie functionality
 */
@Injectable({
  providedIn: 'root',
})
export class CookieService {
  private readonly isBrowser: boolean = false;
  private data: any[] = [];

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.isBrowser = true;
    }
  }

  /**
   * Get a cookie by name
   * @param {string} name Name of the cookie you want to retrieve
   * @return {string} Returns the value of the given cookie name if exists or an empty string if it doesn't
   */
  public get(name: string): string {
    if (!this.isBrowser) {
      return name in this.data ? this.data[name] : '';
    }
    const ca: Array<string> = document.cookie.split(';');
    const caLen: number = ca.length;
    const cookieName = `${name}=`;
    let c: string;

    for (let i = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      if (c.startsWith(cookieName)) {
        return c.substring(cookieName.length, c.length);
      }
    }
    return '';
  }

  /**
   * Get a cookie by pattern
   * @param {RegExp} pattern Pattern to match the cookie name
   * @return {string} Returns the value of the first matching cookie if exists or an empty string if it doesn't
   */
  public getByPattern(pattern: RegExp): string {
    if (!this.isBrowser) {
      for (const key of this.data) {
        if (Object.prototype.hasOwnProperty.call(this.data, key) && pattern.test(key)) {
          return this.data[key];
        }
      }
      return '';
    }
    const ca: Array<string> = document.cookie.split(';');
    const caLen: number = ca.length;
    let c: string;

    for (let i = 0; i < caLen; i += 1) {
      c = ca[i].replace(/^\s+/g, '');
      const match = pattern.exec(c);
      if (match) {
        return c.substring(match[0].length + 1, c.length);
      }
    }
    return '';
  }

  public set(name: string, value: string, expireDays: number, path = '', domain?: string) {
    if (!this.isBrowser) {
      this.data[name] = value;
      return;
    }
    const d = new Date();
    d.setTime(d.getTime() + expireDays * 24 * 60 * 60 * 1000);
    const expires = `expires=${d.toUTCString()}`;
    const cPath: string = path ? `; path=${path}` : '';
    const domainString = domain ? `;domain=${domain}` : '';
    document.cookie = `${name}=${value}; ${expires}${cPath}${domainString}`;
  }
}
